import type {
  Product,
  Order,
  Category,
  Variant,
  Price,
  ProductCategory,
  WishlistResponseData,
  AppliedReduction,
  BasketItem,
  ProductSearchQuery,
} from '@scayle/storefront-nuxt'
import {
  getAppliedReductionsByCategory,
  getFirstAttributeValue,
  getLowestPrice,
  getOriginalPrice,
  getPrice,
  getLatestCategory,
} from '@scayle/storefront-nuxt'
import { isEqual } from 'es-toolkit'
import type {
  ProductActionData,
  ProductInfo,
  ProductListData,
  ProductViewData,
  TrackingEvent,
  TrackingPayload,
  AdditionalInfo,
  ViewInfo,
  AdditionalTrackingEvent,
  MultipleActionData,
  EcommerceTrackingEvent,
  EcommerceTrackingPayload,
  CheckoutNativeEcommerceItem,
} from '../types'
import { ECOMMERCE_TRACKING_EVENTS } from '../types'

import useProductTrackingLocalStorage from '../useProductTrackingLocalStorage'
import usePromotionTrackingLocalStorage from '@/composables/tracking/usePromotionTrackingLocalStorage'
import type { RouteLocation } from '#vue-router'
import type { Experiment } from '#layers/base/modules/ab-testing/experiment'
import type { FilterItemWithValues } from '#layers/base/types/filter'
import type { StoryLink } from '~/@types/storyblok'

const PROMOTION_STRATEGY = 'lastTouch'

/**
 * Checks if event is one of: AdditionalTrackingEvent = 'cart' | 'wishlist'
 */
export function isAdditionalTrackingEvent(
  event: TrackingEvent,
): event is AdditionalTrackingEvent {
  return ['cart', 'wishlist'].includes(<AdditionalTrackingEvent>event)
}

/**
 * Checks if event is one of: 'view_promotion', 'select_promotion'
 */
export function isPromotionTrackingEvent(event: TrackingEvent): boolean {
  return ['view_promotion', 'select_promotion'].includes(event)
}

/**
 * Checks if event is EcommerceTrackingEvent
 */
export function isEcommerceTrackingEvent(
  event: TrackingEvent,
): event is EcommerceTrackingEvent {
  return ECOMMERCE_TRACKING_EVENTS.includes(event as EcommerceTrackingEvent)
}

/**
 * Checks if payload is EcommerceTrackingPayload
 */
export function isEcommerceTrackingPayload(
  payload: EcommerceTrackingPayload | TrackingPayload,
): payload is EcommerceTrackingPayload {
  return (payload as EcommerceTrackingPayload).ecommerce !== undefined
}

/**
 * Checks if payload.items is not undefined
 */
export function isProductImpressionsData(
  data: TrackingPayload | MultipleActionData,
): data is MultipleActionData {
  return (<MultipleActionData>data)?.items !== undefined
}

type ProductData = ProductActionData | ProductViewData | ProductListData
/**
 * Checks if payload.products is not undefined
 */
export function isProductsData(data: TrackingPayload): data is ProductData {
  return (<ProductData>data)?.product !== undefined
}

export const mapProductToTrackingPayload = (
  product: Product,
  variant?: Variant,
): ProductInfo => {
  const price = variant
    ? getPrice(variant)
    : getLowestPrice(product?.variants ?? [])
  const getFloatedReducedPriceForCategoryOrNull = (
    price: Price,
    type: 'sale' | 'campaign',
  ) => {
    const reduction = getAppliedReductionsByCategory(price, type)[0]?.amount
      .absoluteWithTax
    return reduction === undefined ? 0.0 : toFloat(reduction) || 0.0
  }
  return {
    item_id: product.id.toString(),
    item_name: getFirstAttributeValue(product.attributes, 'name')!.label,
    price: toFloat(price.withTax),
    sale_discount: getFloatedReducedPriceForCategoryOrNull(price, 'sale'),
    campaign_discount: getFloatedReducedPriceForCategoryOrNull(
      price,
      'campaign',
    ),
    original_price: toFloat(getOriginalPrice(price)),
    item_brand: getFirstAttributeValue(product.attributes, 'brand')
      ?.label as string,
    item_brand_id: getFirstAttributeValue(
      product.attributes,
      'brand',
    )?.id!.toString() as string,
  }
}

const mapAdditionalInfo = (
  data: ProductActionData | ProductListData | ProductViewData,
): AdditionalInfo | ViewInfo => {
  const { product, list } = data
  const productCategory = getDeepestCategoryForTracking(product.categories)

  let mappedPayload: AdditionalInfo | ViewInfo = {
    item_category:
      (data.category as Category)?.path ||
      data.category?.name ||
      productCategory?.name ||
      '',
    item_category_id: String(data.category?.id || productCategory?.id || ''),
    item_variant: product?.variants?.[0]?.id.toString(),
    item_list_name: list?.name || null,
    item_list_id: list?.id || null,
  }

  if (list?.index && list.index > -1) {
    mappedPayload.index = list.index
  }

  const {
    source = '',
    destination = '',
    destinationUrl = '',
    quantity = 1,
    variant,
  } = data as ProductViewData & ProductActionData

  if (source) {
    mappedPayload = { ...mappedPayload, source }
  }

  if (destination) {
    mappedPayload = { ...mappedPayload, destination }
  }

  if (destinationUrl) {
    mappedPayload = { ...mappedPayload, destination_url: destinationUrl }
  }

  if (quantity > -1) {
    mappedPayload = { ...mappedPayload, quantity: quantity.toString() }
  }

  if (product.isSoldOut) {
    mappedPayload = { ...mappedPayload, sold_out: true }
  }

  if (variant) {
    mappedPayload = { ...mappedPayload, item_variant: variant.id.toString() }
  }

  return {
    ...mappedPayload,
    index:
      'index' in product && (product as any).index > -1
        ? (product.index as unknown as number) + 1
        : 1,
  }
}

const getTotalPriceInfo = (
  items: {
    quantity: number

    campaign_discount: number

    sale_discount: number
    price: number
    priceWithTax: number
  }[],
) => {
  const total = {
    total_campaign_reduction_with_tax: 0.0,
    total_sale_reduction_with_tax: 0.0,
    total_with_tax: 0.0,
    total_without_tax: 0.0,
  }

  items.forEach((item) => {
    total.total_campaign_reduction_with_tax +=
      item.campaign_discount * item.quantity
    total.total_sale_reduction_with_tax += item.sale_discount * item.quantity
    total.total_with_tax += item.priceWithTax * item.quantity
    total.total_without_tax += item.price * item.quantity
  })

  Object.keys(total).forEach(
    (key) =>
      ((total as any)[key] = Number(
        ((total as any)[key] as number).toFixed(2),
      )),
  )

  return total
}

type PromotionStrategy = 'firstTouch' | 'lastTouch'

const emptyPromotion: ActivePromotionData = {
  promotion_id: '',
  promotion_name: '',
  creative_name: '',
  creative_slot: '',
  creative_name_storyblok: '',
}

export const enrichTrackingData = (
  event: TrackingEvent,
  data: TrackingPayloadNew,
  route: RouteLocation,
) => {
  const enrichedData: TrackingPayloadNew = { event, ...data }
  const { getProductPromotion, setProductPromotion, getRoutePromotion } =
    usePromotionTrackingLocalStorage()

  const applyPromotionStrategyFirstTouch = (
    item: EcommerceItem,
    routePromotion?: ActivePromotionData,
  ) => {
    const itemPromotion = getProductPromotion(item.item_id)

    if (!itemPromotion && routePromotion) {
      setProductPromotion(item.item_id, routePromotion)
    }

    return itemPromotion || routePromotion || emptyPromotion
  }

  const applyPromotionStrategyLastTouch = (
    item: EcommerceItem,
    routePromotion?: ActivePromotionData,
  ) => {
    const itemPromotion = getProductPromotion(item.item_id)

    if (routePromotion) {
      setProductPromotion(item.item_id, routePromotion)
    }

    return routePromotion || itemPromotion || emptyPromotion
  }

  const enrichPromotionData = (
    item: EcommerceItem,
    strategy: PromotionStrategy,
  ) => {
    const routePromotion = getRoutePromotion(route)

    // Set route promotion for an item using the last touch strategy:
    // If a route has a promotion, it takes precedence over any other promotion
    // (it is the last touch)
    return strategy === 'lastTouch'
      ? applyPromotionStrategyLastTouch(item, routePromotion)
      : applyPromotionStrategyFirstTouch(item, routePromotion)
  }

  if (isEcommerceTrackingPayload(enrichedData)) {
    const { items } = enrichedData.ecommerce
    enrichedData.ecommerce.items = items.map((item) => {
      const promotion = enrichPromotionData(item, PROMOTION_STRATEGY)
      return Object.assign(item, promotion)
    })
  }

  return enrichedData
}

// @todo refactor
/** @deprecated Each tracking event call should be responsible for building the event data.
 * This approach will no longer be used because it was found to be too complex to maintain
 * and not very flexible. Instead, each tracking event call is responsible for building the event data.
 */
export const mapTrackingDataForEvent = (
  event: TrackingEvent,
  payload: TrackingPayload,
  url?: string,
  experiments?: Experiment[],
) => {
  let data = {}

  if (isPromotionTrackingEvent(event)) {
    data = {
      ecommerce: payload,
    }
  } else if (
    isAdditionalTrackingEvent(event) &&
    isProductImpressionsData(payload)
  ) {
    const items = payload.items.map((item) => ({
      ...mapProductToTrackingPayload(item.product),
      ...mapAdditionalInfo(item),
    }))

    // @ts-expect-error - data is loosely typed and accepts arbitrary tracking item shapes
    data.items = items
    const totalPrice = getTotalPriceInfo(
      items.map((item) => ({
        price: item.price,
        quantity: item.quantity ? parseInt(item.quantity!) : 1,
        campaign_discount: item.campaign_discount,
        sale_discount: item.sale_discount,
        priceWithTax: item.original_price,
      })),
    )

    data = {
      ...data,
      ...totalPrice,
    }
  } else if (isProductImpressionsData(payload)) {
    const currency = 'currencyCode' in payload && payload.currencyCode

    data = {
      ...('user' in payload && { user: payload.user }),
      ecommerce: {
        ...(currency ? { currency } : {}),
        items: payload.items.map((item) => {
          if (item.product) {
            return {
              ...mapProductToTrackingPayload(item.product),
              ...mapAdditionalInfo(item),
              ...(item.product.referenceKey && {
                item_category5: item.product.referenceKey, // needed to properly check promotions down the road
              }),
            }
          }

          return item
        }),
      },
    }
  } else if (isProductsData(payload)) {
    const currency = (payload as typeof payload & { currencyCode: string })
      .currencyCode

    const variant: Variant =
      (payload as any).variant || payload.product.variants?.[0]

    data = {
      ecommerce: {
        ...(currency ? { currency } : {}),
        items: [
          {
            ...mapProductToTrackingPayload(
              payload.product,
              'variant' in payload ? payload.variant : undefined,
            ),
            ...mapAdditionalInfo(payload),
            ...(payload.product.referenceKey && {
              item_category5: payload.product.referenceKey,
            }),
            // For remove_from_wishlist event...
            ...(variant?.stock && {
              stock: variant?.stock.quantity,
            }),
            ...(variant?.referenceKey &&
              getSizeKey(variant.referenceKey) && {
                size_key: getSizeKey(variant.referenceKey),
              }),
          },
        ],
      },
    }
  } else {
    data = {
      ...payload,
    }
  }

  return {
    event,
    ...data,
    ...(experiments?.length && {
      experiments: mapRunningExperimentsTrackingData(experiments),
    }),
    ...(isEcommerceTrackingEvent(event) &&
      import.meta.client && {
        ...mapEcommerceTrackingData(data, url || ''),
      }),
  }
}

export const mapRunningExperimentsTrackingData = (
  experiments: Experiment[],
) => {
  return experiments.map((i) => ({
    [i.name]: i.selectedVariant,
  }))
}

export const mapEcommerceTrackingData = (
  payload: TrackingPayload | EcommerceTrackingPayload,
  url?: string,
): TrackingPayload | EcommerceTrackingPayload => {
  if (!isEcommerceTrackingPayload(payload)) {
    return payload
  }

  const { getProductPromotion, getStoredBanner } =
    usePromotionTrackingLocalStorage()

  const { getStoredProductTrackingItem } = useProductTrackingLocalStorage()

  const { attributes, ecommerce } = payload
  const ecommerceItems = ecommerce.items.map((i) => {
    const itemKey = i.item_category5?.split('-')[0]
    return {
      // inserts promotion data from local storage
      ...(itemKey &&
        (getProductPromotion(itemKey) || getStoredBanner(url || '', itemKey))),
      // inserts product tracking data from local storage
      ...(itemKey && getStoredProductTrackingItem(itemKey)),
      ...(i.item_category5 &&
        attributes &&
        i.item_category5 === attributes.sku && {
          stock: attributes?.stock,
        }),
      ...(i.item_category5 &&
        getSizeKey(i.item_category5) && {
          size_key: getSizeKey(i.item_category5),
        }),
      // prevent overwriting of data (e.g. item_category coming from a promotion) by using this order
      ...i,
    }
  })

  return {
    attributes,
    ecommerce: {
      ...ecommerce,
      items: ecommerceItems,
    },
  }
}

/**
 *
 * @param categories
 * @returns most specific category for a product
 */
export function getDeepestCategory(
  categories: ProductCategory[][] = [],
): ProductCategory | null {
  if (!categories.length) {
    return null
  }
  let depth = { index: 0, value: 0 }
  categories.forEach((category, index) => {
    if (category.length > depth.value) {
      depth = { index, value: category.length }
    }
    if (category.length === depth.value) {
      depth.index = index
    }
  })

  const deepestCategory = categories[depth.index]
  return deepestCategory?.[deepestCategory.length - 1] ?? null
}

export function getDeepestCategoryForTracking(
  categories: ProductCategory[][] = [],
): TrackingCategory & { url?: string } {
  const { categoryName, categoryId, categoryUrl } = getDeepestCategory(
    categories,
  ) || {
    categoryName: '',
    categoryId: '',
  }
  return {
    name: categoryName,
    id: (categoryId || '').toString(),
    url: categoryUrl,
  }
}

export function getFirstDeepestCategoryForTracking(
  categories: ProductCategory[][] = [],
) {
  if (!categories) {
    return undefined
  }

  return categories.reduce((acc, category) => {
    const lastElement = category.slice(-1)[0] as ProductCategory
    if (
      !acc?.categoryUrl ||
      lastElement?.categoryUrl.includes(acc?.categoryUrl)
    ) {
      return lastElement
    }
    return acc
  }, {} as ProductCategory)
}

export function getCategoryLevels(categories: ProductCategory[][] = []) {
  if (!categories?.length) {
    return {}
  }

  const deepestPath = categories.reduce(
    (deepest, current) => (current.length > deepest.length ? current : deepest),
    [],
  )

  return {
    item_category: deepestPath[0]?.categoryName || '',
    item_category2: deepestPath[1]?.categoryName || '',
    item_category3: deepestPath[2]?.categoryName || '',
  }
}

/**
 * Compares data of type WishlistResponseData (wishlist or basket)
 * @param oldData previous data state (eg wishlist data {items, key})
 * @param newData new data state (eg wishlist data {items, key})
 * @returns boolean if a tracking significant change is detected
 */
export function didWishlistOrBasketDataChange(
  oldData: WishlistResponseData | null,
  newData: WishlistResponseData | null,
) {
  return !isEqual(
    {
      items: oldData?.items.map((item) => ({
        productId: item.product?.id,
        variantId: item.variant?.id,
        price: item.product?.priceRange,
        quantity: (item as any).quantity,
        soldOut: item.product?.isSoldOut,
      })),
      key: oldData?.key,
    },
    {
      items: newData?.items.map((item) => ({
        productId: item.product?.id,
        variantId: item.variant?.id,
        price: item.product?.priceRange,
        quantity: (item as any).quantity,
        soldOut: item.product?.isSoldOut,
      })),
      key: newData?.key,
    },
  )
}

// same as formatPrice from @scayle/storefront-nuxt2 without using the useContext
export const formatPriceWithCurrency = (
  value: number,
  locale: string,
  currency: string,
): string => {
  const currencyFractionDigits = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).resolvedOptions().maximumFractionDigits

  return toFloat(value).toLocaleString(locale, {
    minimumFractionDigits: currencyFractionDigits,
  })
}

export const sumReductions = (reductions: AppliedReduction[]) => {
  if (!reductions) {
    return 0
  }
  return reductions.reduce((sum: number, reduction: AppliedReduction) => {
    return sum + reduction.amount.absoluteWithTax
  }, 0)
}

export const sumReductionsByCategory = (
  reductions?: AppliedReduction[],
  category?: AppliedReduction['category'],
) => {
  if (!reductions) {
    return 0
  }
  return sumReductions(
    reductions.filter((reduction) => reduction.category === category),
  )
}

export const sumReductionsFromAllOrderItemsPerCategory = (
  orderItems: Order['items'],
  category: AppliedReduction['category'],
) => {
  if (!orderItems) {
    return 0
  }
  return orderItems.reduce((sum: number, orderItem: any) => {
    return (
      sum +
      sumReductionsByCategory(orderItem?.price?.appliedReductions, category)
    )
  }, 0)
}

export const getFirstCarrierKey = (orderData: any) => {
  return orderData.packages?.[0]?.carrierKey
}

// gift card is not handled yet, so for now is just a placeholder
export const getGiftcardAmount = ({
  amount,
  currency,
  locale,
}: {
  amount: number
  currency: string
  locale: string
}) => {
  return new Intl.NumberFormat(locale.replace('_', '-'), {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(amount)
}

export const businessUnits: { [index: string]: string } = {
  de: 'Deutschland',
  at: 'Österreich',
  ch: 'Schweiz_DE',
  'fr-ch': 'Schweiz_FR',
  'fr-be': 'Belgien_FR',
  fr: 'Frankreich',
  be: 'Belgien_NL',
  nl: 'Niederlande',
  fi: 'Finnland',
}

export const getPageCategoryForTracking = (
  route: RouteLocation,
  categoryPaths: string[],
): string => {
  const categories = [
    'home',
    'cart',
    'checkout',
    'orderConfirmation',
    'product',
    'searchResult',
    'ags',
    'support',
    'customerService',
    'other',
    'wishlist',
    'signin',
    'account',
    'blog',
  ]

  const routeWithoutLocale = route.path.replace(/^\/\w+/, '')

  if (categoryPaths.includes(routeWithoutLocale)) {
    return `category_${routeWithoutLocale.split('/').length - 1}`
  }

  return (
    categories.find((category) => route.path.includes(category)) ||
    ((route.name as string).includes('index') && 'home') ||
    ((route.name as string).includes('p-slug-id') && 'product') ||
    (route.path.includes('search') && 'searchResult') ||
    (route.path.includes('success') && 'orderConfirmation') ||
    (route.path.includes('basket') && 'cart') ||
    (route.path.includes('service') && 'customerService') ||
    (route.path.includes('magazin') && 'blog') ||
    'other'
  )
}

export const getEmailHash = async (email: string | undefined) => {
  if (!email) {
    return ''
  }
  const hashBuffer = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(email.replace(/ /g, '')?.toLowerCase()),
  )
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

export const getSizeKey = (sku: string): string | undefined => {
  return sku.split('-')[1] || undefined
}

export const getMainCategory = (product: Product | null) => {
  const mainCategories = product?.categories

  // if still not found use the latest
  if (!mainCategories || !mainCategories.length) {
    return getLatestCategory(product?.categories || [])
  }

  const fullCategoryPath = mainCategories[mainCategories.length - 1]
  if (!fullCategoryPath) {
    return undefined
  }
  return fullCategoryPath[fullCategoryPath.length - 1]
}

export const getBasketItemFromCoEvent = (
  items: BasketItem<Product, Variant>[] = [],
  eventData: { ecommerce: { items: CheckoutNativeEcommerceItem[] } },
) => {
  const coItems = eventData?.ecommerce?.items ?? []
  const coItem = coItems[0]
  if (!coItem) {
    return undefined
  }
  return items.find(
    (item) =>
      item.product.id.toString() === coItem.item_id &&
      item.variant.id.toString() === coItem.item_variant,
  )
}

/*
  Builds a string that represents comma-separated active filters.

  Each filter is represented as a key-value pair.
  The key is the filter name and the value is the string representation of the filter value.
  Eg. 'colorRange:rot,colorRange:braun,sale:true,brand:goldner'

  @param activeFilters - The active filters
  @returns The filter options string
*/
export const getFilterOptions = (
  filters: FilterItemWithValues[] | null | undefined,
  appliedFilter: ProductSearchQuery,
  unfilteredFilters?: FilterItemWithValues[] | null,
) => {
  const { attributes, minPrice, maxPrice, term } = appliedFilter
  const filterOptions: Array<unknown> = []

  if (minPrice) {
    filterOptions.push(`minPrice:${toFloat(minPrice)}`)
  }
  if (maxPrice) {
    filterOptions.push(`maxPrice:${toFloat(maxPrice)}`)
  }
  if (term) {
    filterOptions.push(`term:${term}`)
  }

  if (!filters || !Array.isArray(filters)) {
    return filterOptions.join(',')
  }

  // ECOM-2675: gate booleans against the unfiltered filter universe (mirrors
  // getFilterNames). availableFilters drops a zero-count boolean (e.g. sale)
  // after the current filter combination, so the unfiltered set is the backstop
  // that keeps a real boolean, while non-filter true/false params (?htf.x=false,
  // consent keys) — which never appear as SCAYLE filter slugs — are rejected.
  const booleanSlugs = new Set([
    ...filters.map((filter) => filter.slug),
    ...(Array.isArray(unfilteredFilters)
      ? unfilteredFilters.map((filter) => filter.slug)
      : []),
  ])

  attributes?.forEach((attribute) => {
    if (!attribute.key) {
      return
    }

    if (attribute.type === 'boolean') {
      if (!booleanSlugs.has(attribute.key)) {
        return
      }
      filterOptions.push(`${attribute.key}:${attribute.value}`)
    } else if (attribute.type === 'attributes') {
      const filterValues =
        filters.find((filter) => filter.slug === attribute.key)?.values ?? []
      const options = attribute.values
        .map(
          (appliedValue) =>
            (filterValues as AttributesFilterValue[]).find(
              (value) => value.id === appliedValue,
            )?.value,
        )
        .filter(Boolean)
      filterOptions.push(options.map((option) => `${attribute.key}:${option}`))
    }
  })

  return filterOptions.join(',')
}

// ECOM-2675: `getFilterNames` lives in its own module (type-only imports) so it
// stays unit-testable in isolation — importing this barrel pulls in `import.meta`
// usage that the jest environment can't evaluate.
export { getFilterNames } from './getFilterNames'

const routeToPromotionTypeMap: Record<PageType, TrackPromotionPageType> = {
  home: 'home',
  pdp: 'pdp',
  detail: 'pdp',
  category: 'productlist',
  outfit: 'outfit',
  outfit_overview: 'outfit',
}

export const resolvePageType = (route: RouteLocation) => {
  const routeType = route.meta?.pageType as PageType | undefined

  if (!routeType) {
    return undefined
  }

  const resolvedType = routeToPromotionTypeMap[routeType] as
    | TrackPromotionPageType
    | undefined
  if (!resolvedType) {
    return routeType
  }

  return resolvedType
}

export const resolveTilePromoType = (story?: StoryLink) => {
  const storyName = story?.name?.toLowerCase()
  if (storyName?.includes('outfit')) {
    return 'outfittile'
  }
  if (storyName?.includes('newsletter')) {
    return 'newsletterpromo'
  }
  if (storyName?.includes('inspiration')) {
    return 'inspirationpromo'
  }
  if (storyName?.includes('sale')) {
    return 'saletile'
  }
  return 'fallback_tile'
}

export const urlLocaleRegex =
  /^\/at|^\/be|^\/fr-be|^\/ch|^\/fr-ch|^\/de|^\/fi|^\/fr|^\/nl.*$/

export const DEFAULT_SIZE = 'default'
export const DEFAULT_SIZE_KEY = 'default'
export const DEFAULT_QUANTITY = 1
export const DEFAULT_STOCK = 0
